import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {FiMoreVertical} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import styles from "./ActionsMenu.module.css";

const GAP = 8;

const ActionsMenu = ({duplicate, onEdit, onDeleteFromExamTemplate, onDelete, onDisable, editDisabled, editDisabledHint}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({top: 0, left: 0, place: "down"});

    const wrapperRef = useRef(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    const close = () => setOpen(false);

    const computePosition = () => {
        const btn = btnRef.current;
        const menu = menuRef.current;
        if (!btn || !menu) return;

        const btnRect = btn.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();

        const spaceBelow = window.innerHeight - btnRect.bottom;
        const spaceAbove = btnRect.top;

        const placeUp = spaceBelow < menuRect.height + GAP && spaceAbove > menuRect.height + GAP;

        const top = placeUp ? btnRect.top - menuRect.height - GAP : btnRect.bottom + GAP;

        let left = btnRect.right - menuRect.width;
        left = Math.max(GAP, Math.min(left, window.innerWidth - menuRect.width - GAP));

        setPos({top, left, place: placeUp ? "up" : "down"});
    };

    useLayoutEffect(() => {
        if (!open) return;
        computePosition();
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const onDown = (e) => {
            const insideBtn = btnRef.current && btnRef.current.contains(e.target);
            const insideMenu = menuRef.current && menuRef.current.contains(e.target);
            if (!insideBtn && !insideMenu) close();
        };

        const onKey = (e) => {
            if (e.key === "Escape") close();
        };

        const onRecalc = () => computePosition();

        document.addEventListener("mousedown", onDown);
        document.addEventListener("touchstart", onDown);
        document.addEventListener("keydown", onKey);
        window.addEventListener("scroll", onRecalc, true);
        window.addEventListener("resize", onRecalc);

        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("touchstart", onDown);
            document.removeEventListener("keydown", onKey);
            window.removeEventListener("scroll", onRecalc, true);
            window.removeEventListener("resize", onRecalc);
        };
    }, [open]);

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <button type="button" ref={btnRef} className={styles.menuBtn} onClick={() => setOpen((v) => !v)}>
                <FiMoreVertical size={18}/>
            </button>

            {open &&
                createPortal(
                    <div ref={menuRef} className={styles.menu}
                         style={{position: "fixed", top: pos.top, left: pos.left}}>
                        {
                            duplicate && <button
                                type="button"
                                onClick={() => {
                                    close();
                                    duplicate?.();
                                }}
                            >
                                {t("common.copy")}
                            </button>
                        }
                        <button
                            type="button"
                            disabled={editDisabled}
                            title={editDisabled ? editDisabledHint : undefined}
                            style={editDisabled ? {opacity: 0.5, cursor: "not-allowed"} : undefined}
                            onClick={() => {
                                if (editDisabled) return;
                                close();
                                onEdit?.();
                            }}
                        >
                            {t("common.edit")}
                        </button>
                        {
                            onDeleteFromExamTemplate &&
                            <button
                                type="button"
                                onClick={() => {
                                    close();
                                    onDeleteFromExamTemplate?.();
                                }}
                            >
                                {t("common.removeFromTemplate")}
                            </button>
                        }
                        {onDelete && (
                            <button
                                type="button"
                                onClick={() => {
                                    close();
                                    onDelete?.();
                                }}
                            >
                                {t("common.delete")}
                            </button>
                        )}
                        {onDisable && (
                            <button
                                type="button"
                                onClick={() => {
                                    close();
                                    onDisable?.();
                                }}
                            >
                                {t("common.disable")}
                            </button>
                        )}
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default ActionsMenu;
